<template>
  <!-- Custom drawer that mimics VNavigationDrawer behavior but preserves original styling -->
  <div v-if="modelValue" class="custom-drawer-overlay" @click="$emit('update:modelValue', false)">
    <div 
      class="offcanvas offcanvas-end show"
      tabindex="-1"
      @click.stop
      style="visibility: visible;"
    >
      <div class="offcanvas-body">
      <div v-if="agendaItems().length == 0" class="empty-state">
        <figure>
          <EmptyStateImage />
        </figure>
        <span>
        <div class="title" v-t="'message.DrawerAgenda.no_meetings_title'"></div>
        <p v-t="'message.DrawerAgenda.no_meetings_message'"></p>
        </span>
        
      </div>
      <template v-else>
       
        <div class="agendaItems">
          <div v-if="conferenceStore.selected?.request_translator" class="translator-request-section">
            <div class="d-flex justify-content-end">
              <TranslatorRequest :conference="conferenceStore.selected" />
            </div>
          </div>
          <template v-for="(meeting, index) in agendaItems()" :key="index">
            <section :class="{ 'new-item': meeting.new_meeting }">
              <div class="col-times">
                <div class="new" v-if="meeting.new_meeting">
                  <i class="bi bi-star-fill"></i>
                  <span v-t="'message.DrawerAgenda.new_meeting_label'"></span>
                  
                </div>
                <div class="date">
                  {{ dayjs(meeting.start).format("MMM D") }}
                </div>
                <div class="time">
                  {{ dayjs(meeting.start).format("h:mm a") }}
                </div>
                <div class="duration">({{ meeting.time_interval }} mins)</div>
              </div>
              <div class="col-item">
                <div class="name">
                  <div class="teacher">{{ meeting.name }}</div>
                  <div class="description">{{ meeting.teacher_classes }}</div>
                </div>
                <div class="cancel">
                  <button @click="meeting.confirm = true">
                  <span v-t="'message.DrawerAgenda.cancel_button'"></span>
                  </button>
                </div>
                <div class="student">
                  <label v-t="'message.DrawerAgenda.student_label'"></label>
                  <div>{{ meeting.child_name }}</div>
                </div>
                <div class="type">
                  <label v-t="'message.DrawerAgenda.meeting_type_location'"></label>
                  <MeetingType :appointment="meeting"></MeetingType>
                </div>
                <div class="commentField" v-if="conferenceStore.selected.allow_comments">
                  <label v-t="'message.DrawerAgenda.comment_label'"></label>
                  <textarea class="form-control" v-model="meeting.comment"></textarea>
                  <div class="commentButton" v-if="conferenceStore.selected.allow_comments">
                    <button
                      @click="saveComment(meeting)"
                      :disabled="meeting.loading"
                      :class="{ 'loading-spinner': meeting.loading }"
                    >
                      <span v-t="'message.DrawerAgenda.save_comment_button'"></span>
                    </button>
                  </div>
                </div>
              </div>
              
              <div class="confirm-cancel" v-if="meeting.confirm">
                <span v-t="'message.DrawerAgenda.confirm_label'"></span>
                <button
                  class="btn btn-primary"
                  :disabled="meeting.loading"
                  @click="cancel(meeting)"
                  :class="{ 'loading-spinner': meeting.loading }"
                >
                  <span v-t="'message.DrawerAgenda.confirm_cancel_button'"></span>
                  {{ meeting.name }}
                </button>
                <button
                  class="btn btn-outline-primary"
                  :disabled="meeting.loading"
                  @click="meeting.confirm = false"
                >
                  <span v-t="'message.DrawerAgenda.do_not_cancel_button'"></span>
                </button>
              </div>
            </section>
            <hr v-if="meeting" />
          </template>
        </div>
        
        <footer>
          <button @click="confirm">
            <span v-t="'message.DrawerAgenda.confirm_agenda_button'"></span>
            <small>
              <div>{{ $t('message.DrawerAgenda.confirm_agenda_message_line1') }}</div>
              <div>{{ $t('message.DrawerAgenda.confirm_agenda_message_line2') }}</div>
            </small>
          </button>
        </footer>
        </template>
      </div>
    </div>
  </div>
</template>
<script setup>
// Props and emits
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue']);

import dayjs from "dayjs";
import MeetingType from "@/components/parent/MeetingType.vue";

import EmptyStateImage from "@/assets/icons/agenda-empty-state.svg";
import TranslatorRequest from "@/components/parent/TranslatorRequest.vue";

import { useTeacherStore } from "@/stores/parent/teacher";
import { useConferenceStore } from "@/stores/parent/conference";
import { useAgendaStore } from "@/stores/parent/agenda";
import { useRouter, useRoute } from "vue-router";

const agendaStore = useAgendaStore();
const teacherStore = useTeacherStore();
const conferenceStore = useConferenceStore();
const router = useRouter();
const route = useRoute();

async function confirm() {
  agendaStore.pendingConfirmation = true;
  agendaStore.getEmail(conferenceStore.selected.conference_id);
  router.push("/" + route.params.account);
}

function saveComment(meeting) {
  const meeting_detail = {
    pta_id: meeting.pta_id,
    message: meeting.comment
  };
  meeting.loading = true;
  agendaStore.saveDetails(meeting_detail).then((resp) => {
    agendaStore.getById(parseInt(resp.pta_id)).comment = resp.message;
    meeting.loading = false;
  });
}

function agendaItems() {
  return agendaStore.agenda
    .filter((meeting) => meeting.conference_id == conferenceStore.selected.conference_id)
    .sort((a, b) => {
      return a.start < b.start ? -1 : 1;
    });
}
async function cancel(meeting) {
  meeting.loading = true;
  const status = await agendaStore.cancelMeeting(meeting.pta_id);
  if (status == true) {
    let timeslot;
    if (meeting.teacher_uid) {
      var teacher_index = teacherStore.getIndexById(meeting.teacher_uid);
      var teacher = teacherStore.teachers[teacher_index];
      if (teacher.timeslots === undefined) {
        let index = agendaStore.getMeetingIndexById(meeting.pta_id);
        agendaStore.agenda.splice(index, 1);
        return false;
      }
      timeslot = teacher.timeslots.find(
        (timeslot) =>
          timeslot.id !== undefined && timeslot.type == "reserved" && timeslot.id == meeting.pta_id
      );
    } else {
      var team_index = teacherStore.getTeamIndexById(meeting.team_uid);
      var team = teacherStore.teams[team_index];
      if (team.timeslots === undefined) {
        let index = agendaStore.getMeetingIndexById(meeting.pta_id);
        agendaStore.agenda.splice(index, 1);
        return false;
      }
      timeslot = team.timeslots.find(
        (timeslot) =>
          timeslot.id !== undefined && timeslot.type == "reserved" && timeslot.id == meeting.pta_id
      );
    }
    if (timeslot) {
      timeslot.type = "available";
      timeslot.checked = false;
      timeslot.disabled = false;
    }
    let index = agendaStore.getMeetingIndexById(meeting.pta_id);
    agendaStore.agenda.splice(index, 1);
    let booked_meetings = [];
    agendaStore.agenda
      .filter(
        (meeting) => meeting.conference_days_id == conferenceStore.selectedDay.conference_days_id
      )
      .forEach((meeting) => {
        if (
          meeting.teacher_uid
            ? booked_meetings.push(meeting.teacher_uid)
            : booked_meetings.push(meeting.team_uid)
        );
      });
    teacherStore.selected.forEach((t) => {
      if (!booked_meetings.includes(t.value.uid)) {
        teacherStore.clearDisabledTimeslots(t.value);
        agendaStore.agenda
          .filter(
            (meeting) =>
              meeting.conference_days_id == conferenceStore.selectedDay.conference_days_id
          )
          .forEach((meeting) => {
            teacherStore.disableTimeslots(t.value, conferenceStore.selectedDay, meeting);
          });
      }
    });
  } else {
    console.log("Could not cancel appointment");
  }
}
</script>

<style scoped>
.custom-drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1022;
  display: flex;
  justify-content: flex-end;
}

.offcanvas {
  position: relative;
  z-index: 1023;
  margin: 0;
}

.translator-request-section {
  padding-bottom: 0.75rem;
  margin-bottom: 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}
</style>
